<script setup lang="ts">
import { computed, ref } from 'vue'
import { Color } from '@previo/codebooks'
import PBtn from '../btn/PBtn.vue'
import PContextMenu from '../context-menu/PContextMenu.vue'
import PDivider from '../divider/PDivider.vue'
import PItem from '../list/PItem.vue'
import PList from '../list/PList.vue'
import { usePrevioDS } from '../../composables/helpers'
import type { PBtnDropdownActionType, PBtnDropdownColor } from './types'

type Props = {
  text?: null | string
  icon?: null | string
  title: string
  cy: string
  actions?: PBtnDropdownActionType[]
  color?: PBtnDropdownColor
  disable?: boolean
  loading?: boolean
}

type Emits = {
  (e: 'click', evt: Event): void
  (e: 'open'): void
  (e: 'close'): void
}

const props = withDefaults(defineProps<Props>(), {
  text: null,
  icon: null,
  actions: () => [],
  color: Color.PRIMARY,
  disable: false,
  loading: false,
})

const emits = defineEmits<Emits>()

const ds = usePrevioDS()

const isMenuOpen = ref<boolean>(false)

const hasActions = computed<boolean>(() => props.actions.length > 0)
const isSplit = computed<boolean>(() => ds.screen().isLaptopAndLarge && hasActions.value)
const collapsedText = computed<null | string>(() => {
  return ds.screen().isLaptopAndLarge ? props.text : null
})

const bgClass = computed<null | string>(() => {
  if (!isSplit.value) {
    return null
  }

  if (props.disable) {
    return 'ds-btn-dropdown--disabled'
  }

  return `p-bg-${props.color}`
})

const click = (e: Event): void => {
  isMenuOpen.value = false
  emits('click', e)
}

const open = (): void => {
  isMenuOpen.value = true
}

const close = (): void => {
  isMenuOpen.value = false
}

const toggle = (): void => {
  isMenuOpen.value = !isMenuOpen.value
}

const collapsedClick = (e: Event): void => {
  if (hasActions.value) {
    toggle()

    return
  }

  click(e)
}

defineExpose({
  open,
  close,
  toggle,
})
</script>

<template>
  <div class="ds-btn-dropdown q--avoid-card-border" :class="bgClass">
    <template v-if="isSplit">
      <p-btn
        class="ds-btn-dropdown__main"
        :text="text"
        :color="color"
        :disable="disable"
        :loading="loading"
        :cy="cy"
        @click="click"
      />
      <p-divider :color="Color.WHITE_TRANSPARENT" vertical />
      <p-btn
        class="ds-btn-dropdown__toggle"
        icon-end="chevron-down"
        :color="color"
        :disable="disable"
        :cy="`${cy}-dropdown`"
        aria-haspopup="menu"
        :aria-expanded="isMenuOpen"
        @click="toggle()"
      />
    </template>

    <p-btn
      v-else
      :text="collapsedText"
      :icon-end="icon"
      :color="color"
      :disable="disable"
      :loading="loading"
      :cy="cy"
      :aria-haspopup="hasActions ? 'menu' : undefined"
      :aria-expanded="hasActions ? isMenuOpen : undefined"
      @click="collapsedClick"
    />

    <p-context-menu
      v-if="hasActions"
      v-model="isMenuOpen"
      :title="title"
      :cy="`${cy}-menu`"
      @open="emits('open')"
      @close="emits('close')"
    >
      <p-list>
        <p-item v-if="!isSplit && text" :text="text" :cy="`${cy}-main-item`" @click="click" />
        <p-item
          v-for="action in actions"
          :key="action.cy"
          :text="action.text"
          :icon-start="action.icon"
          :disable="action.disable"
          :cy="action.cy"
          @click="action.onClick"
        />
      </p-list>
    </p-context-menu>
  </div>
</template>

<style lang="scss" scoped>
@import '../../assets/scss/shared/brand.module';
@import '../../assets/scss/shared/variables/ds.radius';

// matches Quasar's $button-border-radius (8px)
$btn-dropdown-radius: $radius-md-mobile;

.ds-btn-dropdown {
  display: inline-flex;
  border-radius: $btn-dropdown-radius;
}

.ds-btn-dropdown--disabled {
  background-color: getColor('grey300');
}

.ds-btn-dropdown__main {
  border-radius: $btn-dropdown-radius 0 0 $btn-dropdown-radius;
}

.ds-btn-dropdown__toggle {
  border-radius: 0 $btn-dropdown-radius $btn-dropdown-radius 0;
}
</style>
